import { Box, Group, Menu, UnstyledButton, createStyles } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownLeft, ChevronRight, Dots, Icon } from 'tabler-icons-react';
import { SubLink } from '../../../type/navlink.types';

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef('icon');

  return {
    control: {
      display: 'block',
      alignItems: 'center',
      textDecoration: 'none',
      fontSize: theme.fontSizes.md,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[1]
          : theme.colors.gray[7],
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,

      '&:hover': {
        backgroundColor:
          theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,

        [`& .${icon}`]: {
          color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        },
      },
    },

    chevron: {
      transition: 'transform 200ms ease',
    },

    link: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      fontSize: theme.fontSizes.md,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[1]
          : theme.colors.gray[7],
      borderRadius: theme.radius.sm,
      fontWeight: 500,
      '&:hover': {
        backgroundColor:
          theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,

        [`& .${icon}`]: {
          color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        },
      },
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    },

    linkIcon: {
      ref: icon,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[2]
          : theme.colors.gray[6],
      marginRight: theme.spacing.sm,
    },

    linkActive: {
      '&, &:hover': {
        backgroundColor: theme.fn.variant({
          variant: 'light',
          color: theme.primaryColor,
        }).background,
        color: theme.fn.variant({ variant: 'light', color: theme.primaryColor })
          .color,
        [`& .${icon}`]: {
          color: theme.fn.variant({
            variant: 'light',
            color: theme.primaryColor,
          }).color,
        },
      },
    },
  };
});

interface LinksGroupProps {
  icon: Icon;
  initiallyOpened?: boolean;
  links?: { label: string; link: string }[];
  active: string;
  setActive: (label: string) => void;
}

function LinksGroup({
  icon: Icon,
  initiallyOpened,
  links,
  active,
  setActive,
}: LinksGroupProps) {
  const { classes, cx, theme } = useStyles();
  const hasLinks = Array.isArray(links);
  const [opened, setOpens] = useState(initiallyOpened || false);
  const ChevronIcon = theme.dir === 'ltr' ? ChevronRight : ChevronDownLeft;
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!initialLoad) {
      return;
    }

    if (JSON.stringify(links).includes(active)) {
      setOpens(true);
    }

    return () => {
      setInitialLoad(false);
    };
  }, [active]);

  const items = (hasLinks ? links : []).map((link) => {
    return (
      <Menu.Item
        p={0}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        <Link
          className={cx(classes.link, {
            [classes.linkActive]: link.label === active,
          })}
          to={link.link}
          onClick={() => {
            setActive(link.label);
          }}
          key={link.label}
        >
          {link.label}
        </Link>
      </Menu.Item>
    );
  });

  const updateSideBar = () => {
    setOpens((prevOpened) => !prevOpened);
  };

  return (
    <>
      {hasLinks ? (
        <Menu
          styles={{
            itemInner: { width: '100%' },
            itemBody: { width: '100%' },
            itemLabel: { width: '100%' },
          }}
          control={
            <UnstyledButton
              onClick={() => updateSideBar()}
              className={classes.control}
            >
              <Group position='apart' spacing={0}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Icon
                    style={{ alignItems: 'center' }}
                    className={classes.linkIcon}
                    strokeWidth={1.5}
                  />
                </Box>
                <Dots className={classes.linkIcon} />
              </Group>
            </UnstyledButton>
          }
        >
          {items}
        </Menu>
      ) : null}
    </>
  );
}

interface NavbarLinksIconGroupProps {
  icon: Icon;
  sublinks: SubLink[];
  active: string;
  setActive: (label: string) => void;
}

const NavbarLinksIconGroup = ({
  icon,
  sublinks,
  active,
  setActive,
}: NavbarLinksIconGroupProps) => {
  return (
    <>
      <LinksGroup
        icon={icon}
        links={sublinks}
        active={active}
        setActive={setActive}
      />
    </>
  );
};

export default NavbarLinksIconGroup;
